// Depth for health bars: above all game objects and overlay layers, except damage text and windows
const HEALTH_BAR_DEPTH = 1001;
import Phaser from "phaser";
import { COLORS, hexToPhaser } from "./colors";

/**
 * HealthBar UI component for Phaser games.
 * Shows health changes with a white animated overlay.
 */
export class HealthBar {
  private scene: Phaser.Scene;
  private x: number;
  private y: number;
  private width: number;
  private height: number;
  private maxHealth: number;
  private currentHealth: number;
  private barColor: number;
  public bar: Phaser.GameObjects.Graphics;
  public overlay: Phaser.GameObjects.Graphics;
  public nameText?: Phaser.GameObjects.Text;
  private hideTimer?: Phaser.Time.TimerEvent;
  private hiddenByDefault: boolean;
  private scale: number;
  private tickInterval?: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    maxHealth: number,
    hiddenByDefault: boolean = false,
    name?: string,
    scale: number = 1,
    barColor?: number,
    tickInterval?: number,
  ) {
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
    this.maxHealth = maxHealth;
    this.currentHealth = maxHealth;
    this.hiddenByDefault = hiddenByDefault;
    this.scale = scale;
    this.barColor = barColor ?? hexToPhaser(COLORS.GREEN);
    this.tickInterval = tickInterval;
    this.bar = scene.add.graphics();
    this.bar.setDepth(HEALTH_BAR_DEPTH);
    this.overlay = scene.add.graphics();
    this.overlay.setDepth(HEALTH_BAR_DEPTH);

    // Create name text if name is provided
    if (name) {
      this.setName(name);
    }

    this.drawBar();

    // Hide by default if specified
    if (hiddenByDefault) {
      this.hide();
    }
  }

  /**
   * Updates the maximum value and redraws the bar.
   */
  setMaxHealth(newMax: number) {
    this.maxHealth = newMax;
    this.currentHealth = Math.min(this.currentHealth, newMax);
    this.drawBar();
  }

  /**
   * Sets the health and animates the change.
   */
  setHealth(newHealth: number) {
    newHealth = Phaser.Math.Clamp(newHealth, 0, this.maxHealth);
    if (newHealth < this.currentHealth) {
      // Animate white overlay for lost health
      this.animateOverlay(this.currentHealth, newHealth);
    }
    this.currentHealth = newHealth;
    this.drawBar();
  }

  /**
   * Draws the main health bar.
   */
  private drawBar() {
    this.bar.clear();
    // Background
    this.bar.fillStyle(0x222222);
    this.bar.fillRect(this.x, this.y, this.width, this.height + 2);

    // Health
    const healthWidth = (this.currentHealth / this.maxHealth) * this.width;
    this.bar.fillStyle(this.barColor);
    this.bar.fillRect(this.x, this.y, healthWidth, this.height);

    // Tick marks
    if (this.tickInterval && this.maxHealth > 0) {
      this.bar.fillStyle(0x00eeff);
      for (let i = this.tickInterval; i < this.maxHealth; i += this.tickInterval) {
        const tickX = Math.round(this.x + (i / this.maxHealth) * this.width);
        this.bar.fillRect(tickX, this.y, 1, this.height);
      }
    }
  }

  /**
   * Animates a white overlay for health loss.
   */
  private animateOverlay(oldHealth: number, newHealth: number) {
    this.overlay.clear();
    const oldWidth = (oldHealth / this.maxHealth) * this.width;
    const newWidth = (newHealth / this.maxHealth) * this.width;
    // Start with white overlay covering lost health
    this.overlay.fillStyle(0xffffff);
    this.overlay.fillRect(
      this.x + newWidth,
      this.y,
      oldWidth - newWidth,
      this.height,
    );

    // Animate the width of the white overlay to 0, moving left
    this.scene.tweens.addCounter({
      from: oldWidth - newWidth,
      to: 0,
      duration: 400,
      onUpdate: (tween) => {
        const value = tween.getValue();
        this.overlay.clear();
        if (typeof value === "number" && value > 0) {
          this.overlay.fillStyle(0xffffff);
          this.overlay.fillRect(this.x + newWidth, this.y, value, this.height);
        }
      },
      onComplete: () => {
        this.overlay.clear();
      },
    });
  }

  /**
   * Shows the health bar and optionally hides it after a delay.
   */
  show(hideAfterMs?: number) {
    this.bar.setVisible(true);
    this.overlay.setVisible(true);
    // Note: nameText visibility is independent of the bar's — the name label
    // stays on screen at all times once set, regardless of damage-flash timing.

    // Clear any existing hide timer
    if (this.hideTimer) {
      this.hideTimer.remove();
      this.hideTimer = undefined;
    }

    // Set up new hide timer if duration specified
    if (hideAfterMs !== undefined && hideAfterMs > 0) {
      this.hideTimer = this.scene.time.delayedCall(hideAfterMs, () => {
        this.hide();
        this.hideTimer = undefined;
      });
    }
  }

  /**
   * Hides the health bar.
   */
  hide() {
    this.bar.setVisible(false);
    this.overlay.setVisible(false);
    // Note: nameText is intentionally left visible — see show() above.

    // Clear any pending hide timer
    if (this.hideTimer) {
      this.hideTimer.remove();
      this.hideTimer = undefined;
    }
  }

  /**
   * Updates the name text displayed above the health bar.
   */
  setName(name: string) {
    if (!this.nameText) {
      // If no name text exists, create it
      this.nameText = this.scene.add.text(
        this.x + this.width / 2,
        this.y - 6 * this.scale,
        name,
        {
          fontSize: "10px",
          color: "#ffffff",
          fontFamily: "Arial",
          stroke: "#000000",
          strokeThickness: 2,
        },
      );
      this.nameText.setOrigin(0.5, 1);
      this.nameText.setDepth(HEALTH_BAR_DEPTH);
    } else {
      // Update existing name text
      this.nameText.setText(name);
    }
  }

  /**
   * Shows the health bar temporarily (4 seconds by default).
   */
  showTemporarily(durationMs: number = 4000) {
    this.show(durationMs);
  }

  /**
   * Destroys the health bar graphics.
   */
  destroy() {
    // Clear any pending hide timer
    if (this.hideTimer) {
      this.hideTimer.remove();
      this.hideTimer = undefined;
    }
    this.bar.destroy();
    this.overlay.destroy();
    if (this.nameText) {
      this.nameText.destroy();
    }
  }
}
